import React, { useMemo, useState, useEffect } from 'react';
import RDKaart from './RDKaart';
import KaartLegenda from './KaartLegenda';
import { getDeelgebieden } from '../api/api';

// Mapping van gebied_categorie (1, 2, 4) in keur_deelgebieden naar onze polder-styling-keys
const POLDER_TYPE_PER_CATEGORIE = { 1: 'polder_1', 2: 'polder_2', 4: 'polder_4' };

/**
 * KaartMetLegenda — bundelt RDKaart + KaartLegenda met gedeelde zichtbaarheids-state.
 *
 * Haalt daarnaast de polder-deelgebieden op uit /publiek/deelgebieden.geojson
 * en mengt ze in de overlays zodat de polder_1/2/4 toggles in de legenda werken.
 *
 * Default-strategie:
 *   - Bij init (legenda nog niet opgestart) → alle overlays tonen, geen flicker
 *   - Onbekende types (geen styling-rij) → default zichtbaar (`!== false`)
 *
 * puntLegenda wordt doorgegeven aan KaartLegenda: de soorten markers horen in
 * dezelfde legenda als de gebiedslagen, niet in een eigen balkje ernaast.
 *
 * legendaZetAan gaat dezelfde kant op: een aanroeper die weet dat een bepaalde
 * laag ertoe doet — bijvoorbeeld het waterstaatswerk waar zojuist in geklikt is
 * — kan die aanzetten zonder de legenda-state zelf te beheren.
 */
export default function KaartMetLegenda({ overlays = [], puntLegenda = [], legendaZetAan = null, ...rdKaartProps }) {
  const [zichtbaarPerType, setZichtbaarPerType] = useState(null);
  const [polderOverlays, setPolderOverlays] = useState([]);

  // Eénmalige fetch van deelgebieden bij mount. Faalt stil; bij netwerkfout
  // verschijnen polders simpelweg niet op de kaart (geen toast/dialog).
  useEffect(() => {
    let canceled = false;
    getDeelgebieden().then(({ data }) => {
      if (canceled) return;
      const features = data?.features || [];
      const transformed = features
        .map(f => {
          const cat = f.properties?.gebied_categorie;
          const type = POLDER_TYPE_PER_CATEGORIE[cat];
          if (!type || !f.geometry) return null;
          return {
            type,
            geometrie: f.geometry,
            naam: f.properties?.gebied_label || `Polder categorie ${cat}`,
          };
        })
        .filter(Boolean);
      setPolderOverlays(transformed);
    }).catch(e => {
      console.warn('Deelgebieden ophalen mislukt:', e);
    });
    return () => { canceled = true; };
  }, []);

  const overlaysGefiltered = useMemo(() => {
    const alle = [...overlays, ...polderOverlays];
    if (zichtbaarPerType === null) return alle;
    return alle.filter(g => zichtbaarPerType[g.type] !== false);
  }, [overlays, polderOverlays, zichtbaarPerType]);

  return (
    <>
      <RDKaart overlays={overlaysGefiltered} {...rdKaartProps} />
      <KaartLegenda onZichtbaarheidChange={setZichtbaarPerType} puntLegenda={puntLegenda}
        zetAan={legendaZetAan} />
    </>
  );
}
